import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { NotificationManager } from 'react-notifications';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import Modal from '../../components/shared/Modal/Modal';
import TooltipContainer from '../../components/shared/TooltipContainer/TooltipContainer';
import InfoTooltip from '../../components/shared/InfoTooltip/InfoTooltip';
import Badge from '../../components/shared/Badge/Badge';
import { formatRating } from '../../components/shared/Rating';
import WarningOutlineIcon from '../../components/svgs/warningOutlineIcon';
import { getImageUrl } from '../../components/shared/ImagePreview/ImagePreview';
import { FileScope } from '../../util/appEnum';
import { formatDate } from '../../util';
import appState from '../../state/AppStateContainer';
import BtButton from '../bt-button/bt-button';
import { ServiceSupplier } from '../../services/service-supplier';
import { fmtKpi } from '../page-sales-tool/company-economics-section';
import {
  fetchSupplierControls, mergeSupplierControls,
  deriveFskatt, deriveMoms, deriveArbetsgivare, deriveSkatteanstand,
  deriveKollektivavtal, deriveKollektivavtalDetails, deriveSanctions, deriveId06,
  deriveArbetsgivaravgifter, deriveHeltidEstimate, deriveBevakad,
  SupplierControlsRaw, SupplierControlsFlags, SupplierControlsSource,
} from './supplierControlsData';
import styles from './CompareSuppliersModal.module.scss';

// A supplier row as loaded into the register's material-table (`this.state.data.data`).
export type CompareSupplierRow = {
  sup_id: string;
  sup_name?: string;
  organizationNumber?: string;
  city?: string;
  rating?: number;
  projectsCount?: number;
  lastRatedAt?: string;
  areaArray?: { name: string }[];
  frameworkAgreements?: FileEntry[];
  liabilityInsurances?: FileEntry[];
  // Credit-service subscription flags from the register row — which providers to query for Kontroll.
  hasSyna?: boolean;
  hasCreditsafe?: boolean;
  hasTic?: boolean;
  logo?: { file?: string };
  // The supplier's own logo lives on `logo`; a logo uploaded via the shared supplier profile
  // lives here instead and needs a different FileScope (see Avatar).
  supplierProfile?: { logo?: { file?: string }; orgNr?: string };
};

type FileEntry = { file?: unknown; validUntilDate?: string | Date };

// Shared shape for the per-supplier async fetches (economics + statistics).
type AsyncEntry = { loading: boolean; data: any | null; error: string | null };

type CityOption = { value: string; label: string };

type Props = {
  show: boolean;
  setShow: (value: boolean) => void;
  suppliers: CompareSupplierRow[];
  // Register's resolved city options, so a city code renders as its label (mirrors the table).
  cities?: CityOption[];
};

// Cap how many Yrkesområde pills render inline before collapsing the rest into a "+N" pill —
// a supplier with a long tail of categories would otherwise blow the row's height out.
const MAX_VISIBLE_PILLS = 5;

// Credit-rating risk tiers, mapped to the same colours the single-supplier Kontroll panel uses
// (.sp-control-row .indicator). Syna grades numerically 1-5, CreditSafe A-E. These hold the
// resolved class rather than a tier name because a CSS-Modules class is hashed at build time and
// so cannot be assembled from a suffix at the call site.
const SYNA_RATING_CLASSES: Record<string, string> = { '1': styles.ratingBadgeHigh, '2': styles.ratingBadgeMedium, '3': styles.ratingBadgeLow, '4': styles.ratingBadgeLow, '5': styles.ratingBadgeLow };
const CREDITSAFE_RATING_CLASSES: Record<string, string> = { E: styles.ratingBadgeHigh, D: styles.ratingBadgeMedium, C: styles.ratingBadgeLow, B: styles.ratingBadgeLow, A: styles.ratingBadgeLow };

// Sentinels the table understands as "nothing to show" / "still loading", rendered muted.
const NO_DATA = '-';
const LOADING = '…';

const errorCode = (e: any): string => e?.response?.data?.error ?? 'unexpected';

const flagsFor = (s: CompareSupplierRow): SupplierControlsFlags =>
  ({ hasSyna: !!s.hasSyna, hasCreditsafe: !!s.hasCreditsafe, hasTic: !!s.hasTic });

// Sources whose fetch failed outright, as opposed to simply having nothing to report.
const failedSources = (raw: SupplierControlsRaw | null | undefined): SupplierControlsSource[] => {
  const errors = raw?.errors ?? {};
  return (Object.keys(errors) as SupplierControlsSource[]).filter(source => errors[source]);
};

/**
 * Resolve a per-supplier async slice into a cell value: a loading marker while the first fetch is
 * in flight, a no-data marker when there's nothing to show, otherwise the caller's projection.
 */
const asyncVal = <D, R>(entry: { loading: boolean; data: D | null } | undefined, get: (d: D) => R): R | string => {
  if (!entry?.data) return entry?.loading ? LOADING : NO_DATA;
  const value = get(entry.data);
  // A fetch is in flight and the data we have gives this cell nothing — it is awaiting the
  // response, so mark it loading (skeleton) rather than "-", which reads as a settled answer.
  // Cells whose current data DOES hold a value keep showing it during a refresh.
  if (entry.loading && (value as unknown) === NO_DATA) return LOADING;
  return value;
};

// A file section (framework agreement / liability insurance) is "active" if any file has a
// validUntilDate in the future. Deliberately simple boolean for the ✔/✕ comparison cell.
const hasActiveFile = (files?: FileEntry[]): boolean =>
  (files ?? []).some(f => f?.file && f?.validUntilDate && new Date(f.validUntilDate).getTime() > Date.now());

// Green check / red cross, matching the "control" (SupplierControls) yes/no indicators.
const Flag: FC<{ active: boolean }> = ({ active }) => (
  <span className={`${styles.flag} ${active ? styles.flagYes : styles.flagNo}`}>{active ? <CheckIcon /> : <CloseIcon />}</span>
);

// Small logo (same source as the detail panel's header image), or a colored initial-letter
// circle when the supplier has none on file — keeps every column visually consistent.
const Avatar: FC<{ s: CompareSupplierRow }> = ({ s }) => {
  // Mirrors supplier-detail-panel.jsx: prefer the shared supplier-profile logo, then the
  // supplier's own. Profile-hosted logos are served under a different scope, keyed by org
  // number rather than supplier id.
  const logoFile = s.supplierProfile?.logo?.file ?? s.logo?.file;
  const isProfileLogo = !!logoFile?.includes('/supplierProfile/');
  const url = logoFile && getImageUrl(
    logoFile,
    appState.getCurrentOrgSlug(),
    isProfileLogo ? FileScope.SupplierProfileLogo : FileScope.SupplierLogo,
    isProfileLogo ? { organizationNumber: s.supplierProfile?.orgNr } : { supplierId: s.sup_id },
  );
  if (url) return <img className={styles.avatar} src={url} alt="" />;
  return <span className={styles.avatarFallback}>{(s.sup_name || '?').trim().charAt(0)}</span>;
};

const CompareSuppliersModal: FC<Props> = ({ show, setShow, suppliers, cities }) => {
  const [t] = useTranslation('supplierDatabase', { keyPrefix: 'compare' });
  // Reuses the exact same key as the single-supplier Kontroll panel's Kollektivavtal info icon
  // (supplier-controls.tsx) instead of duplicating the explanatory text under our own key — one
  // source of truth for that wording.
  const [tControls] = useTranslation('supplierDatabase', { keyPrefix: 'supplierControls' });

  // Local, removable view of the columns (does not touch the register selection). Relies on the
  // parent passing a STABLE `suppliers` reference for the lifetime of one open modal (today:
  // supplier-database.jsx snapshots it into state once, on the "Jämför" click) — if a future
  // change makes the parent recompute `suppliers` inline on unrelated re-renders, this effect
  // would silently discard any locally-removed columns.
  const [visible, setVisible] = useState<CompareSupplierRow[]>(suppliers);
  useEffect(() => { setVisible(suppliers); }, [suppliers]);

  // Per-supplier economics + statistics, keyed by sup_id. Fetched in parallel when the modal opens.
  const [economics, setEconomics] = useState<Record<string, AsyncEntry>>({});
  const [stats, setStats] = useState<Record<string, AsyncEntry>>({});
  // Which refresh button started the section's current activity ('all' | sup_id | null) — see
  // metaRow. null during the initial open, where the fetches run without any button click.
  const [econOrigin, setEconOrigin] = useState<string | null>(null);
  const [ctrlOrigin, setCtrlOrigin] = useState<string | null>(null);
  // Per-supplier "Kontroll" data (Syna/Creditsafe/TIC + sanctions/ID06/collective/monitoring).
  // fetchedAt is stamped client-side on each successful resolve — unlike economics, none of the
  // underlying providers return one shared "as of" timestamp, so this is the only way to show
  // the user that "Hämta senaste" actually did something (values can legitimately come back
  // unchanged if nothing changed at the source since the last fetch).
  const [controls, setControls] = useState<Record<string, { loading: boolean; data: SupplierControlsRaw | null; error: boolean; fetchedAt: Date | null }>>({});

  const fetchEconomics = (supId: string, orgNumber: string | undefined, refresh: boolean) => {
    if (!orgNumber) {
      setEconomics(prev => ({ ...prev, [supId]: { loading: false, data: null, error: 'noOrgNumber' } }));
      return;
    }
    setEconomics(prev => ({
      ...prev,
      [supId]: { data: prev[supId]?.data ?? null, error: refresh ? (prev[supId]?.error ?? null) : null, loading: true },
    }));
    ServiceSupplier.getSupplierEconomics(supId, { refresh })
      .then((data: any) => {
        setEconomics(prev => ({ ...prev, [supId]: { loading: false, data, error: null } }));
        // The backend serves the cached doc flagged refreshFailed when the fresh fetch fails or
        // would downgrade the record (e.g. SYNA unreachable — always the case from dev machines,
        // whose IPs SYNA blocks). Data is still on screen, so this is a warning, not an error.
        if (data?.refreshFailed) {
          NotificationManager.warning(t('error.refreshKeptCached', 'Kunde inte hämta senaste ekonomiska data – visar senast hämtade uppgifter.'));
        } else if (refresh && data?.noNewData) {
          // The provider was reached and answered with exactly the cached content — the
          // timestamp advanced (a real verification happened), so say it succeeded.
          NotificationManager.success(t('refreshNoNewData', 'Förfrågan lyckades: inga nya uppgifter från externa källor.'));
        }
      })
      .catch((error: any) => {
        console.error(error);
        if (refresh) {
          if (economics[supId]?.data) {
            NotificationManager.warning(t('error.refreshKeptCached', 'Kunde inte hämta senaste ekonomiska data – visar senast hämtade uppgifter.'));
          } else {
            NotificationManager.error(t('error.refreshFailed', 'Kunde inte hämta senaste ekonomiska data.'));
          }
          setEconomics(prev => ({
            ...prev,
            [supId]: { data: prev[supId]?.data ?? null, error: prev[supId]?.error ?? null, loading: false },
          }));
        } else {
          setEconomics(prev => ({ ...prev, [supId]: { loading: false, data: null, error: errorCode(error) } }));
        }
      });
  };

  const fetchStats = (supId: string) => {
    setStats(prev => ({ ...prev, [supId]: { data: prev[supId]?.data ?? null, error: null, loading: true } }));
    ServiceSupplier.getSupplierStatistics(supId, {})
      .then((data: any) => setStats(prev => ({ ...prev, [supId]: { loading: false, data, error: null } })))
      .catch((error: any) => {
        console.error(error);
        setStats(prev => ({ ...prev, [supId]: { loading: false, data: null, error: errorCode(error) } }));
      });
  };

  const fetchControls = (s: CompareSupplierRow, refresh: boolean) => {
    if (!s.organizationNumber) {
      setControls(prev => ({ ...prev, [s.sup_id]: { loading: false, data: null, error: false, fetchedAt: null } }));
      return;
    }
    setControls(prev => ({
      ...prev,
      [s.sup_id]: { data: prev[s.sup_id]?.data ?? null, error: false, loading: true, fetchedAt: prev[s.sup_id]?.fetchedAt ?? null },
    }));
    fetchSupplierControls(s.organizationNumber, s.sup_name, flagsFor(s), { creditsafeFresh: refresh })
      .then(data => {
        setControls(prev => ({
          ...prev,
          [s.sup_id]: {
            loading: false,
            data: mergeSupplierControls(prev[s.sup_id]?.data, data, flagsFor(s)),
            error: false,
            fetchedAt: new Date(),
          },
        }));
        // A provider that fails still resolves the fetch (allSettled), so a partial failure has to
        // be reported from the payload — the catch below only fires if the whole call throws.
        // Values from the failed sources are retained by the merge above, hence a warning.
        if (refresh && failedSources(data).length) {
          NotificationManager.warning(t('kontroll.error.refreshKeptCached', 'Kunde inte hämta senaste kontrolldata – visar senast hämtade uppgifter.'));
        }
      })
      .catch((error: any) => {
        console.error(error);
        if (refresh) {
          if (controls[s.sup_id]?.data) {
            NotificationManager.warning(t('kontroll.error.refreshKeptCached', 'Kunde inte hämta senaste kontrolldata – visar senast hämtade uppgifter.'));
          } else {
            NotificationManager.error(t('kontroll.error.refreshFailed', 'Kunde inte hämta senaste kontrolldata.'));
          }
        }
        setControls(prev => ({
          ...prev,
          [s.sup_id]: { data: prev[s.sup_id]?.data ?? null, error: true, loading: false, fetchedAt: prev[s.sup_id]?.fetchedAt ?? null },
        }));
      });
  };

  // Fire cache-first economics + statistics + Kontroll fetches per supplier when the modal opens.
  useEffect(() => {
    if (!show) return;
    // These loads weren't started by a button, so no button should spin for them — without the
    // reset, an origin left over from the previous open would light its button up.
    setEconOrigin(null);
    setCtrlOrigin(null);
    suppliers.forEach(s => {
      fetchEconomics(s.sup_id, s.organizationNumber, false);
      fetchStats(s.sup_id);
      fetchControls(s, false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, suppliers]);

  // `cities` is the full municipality list (~290 entries) and is looked up twice per column
  // (Kommun + Säte), so index it once instead of scanning per cell.
  const cityLabels = useMemo(() => new Map((cities ?? []).map(c => [c.value, c.label])), [cities]);

  // Resolve a kommun code to its label, falling back to the raw value. Returns undefined (not a
  // placeholder string) for empty input so callers can chain their own fallbacks with `||` —
  // returning '-' here would be truthy and silently swallow them.
  const cityLabel = (code?: string): string | undefined =>
    code ? (cityLabels.get(code) || code) : undefined;

  // The section bands stick just below the column header, so their offset has to match the
  // header's rendered height. Measure it instead of hardcoding: the height depends on the
  // avatar and the name line, so a hardcoded constant silently drifts whenever either changes.
  // The SCSS keeps a sensible fallback.
  // Callback refs rather than useRef: Modal delays mounting its children by a tick (see
  // unmountWhenHidden), so a plain ref is still null when an effect keyed on `show` first runs,
  // and would never re-run once the nodes appeared. Holding the nodes in state re-runs this
  // effect exactly when they attach.
  const [headRowEl, setHeadRowEl] = useState<HTMLTableRowElement | null>(null);
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!headRowEl || !scrollEl) return;
    const apply = () => scrollEl.style.setProperty('--compare-head-height', `${headRowEl.offsetHeight}px`);
    // Modal's own show/hide transition (BtModal, nested inside it) can still be mid-flight on
    // the frame these refs attach, so an immediate read can capture a transitional (0px) height
    // that ResizeObserver never corrects afterwards — the row's true size isn't "resizing" from
    // its own perspective, it was just measured too early. Two rAFs push the initial read past
    // that frame; the observer stays on as a safety net for later changes (e.g. a wrapped name).
    const raf = requestAnimationFrame(() => requestAnimationFrame(apply));
    const observer = new ResizeObserver(apply);
    observer.observe(headRowEl);
    return () => { cancelAnimationFrame(raf); observer.disconnect(); };
  }, [headRowEl, scrollEl]);

  const removeColumn = (supId: string) => {
    setVisible(prev => prev.filter(s => s.sup_id !== supId));
  };
  // Removing the last remaining column leaves nothing to compare — close the modal instead of
  // showing an empty table. Deliberately depends on `visible` ONLY, not `setShow`: the parent
  // passes a fresh inline arrow function on every render, and including that unstable reference
  // here made this effect re-run on renders where `visible` hadn't been resynced yet (see the
  // effect above), firing setShow(false) on the very commit that just opened the modal — the
  // reported "first click opens then immediately closes, second click works" bug.
  useEffect(() => {
    if (visible.length === 0) setShow(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Refresh every column of one section at once, so the user needn't click each "Hämta senaste".
  const refreshAll = (refreshOne: (s: CompareSupplierRow) => void) =>
    visible.forEach(s => { if (s.organizationNumber) refreshOne(s); });
  const anyLoading = (slice: Record<string, { loading: boolean }>) => visible.some(s => slice[s.sup_id]?.loading);
  const anyOrgNumber = visible.some(s => s.organizationNumber);

  // Per-section value resolvers over the three async slices.
  const econVal = (s: CompareSupplierRow, get: (d: any) => ReactNode) => asyncVal(economics[s.sup_id], get);
  const statVal = (s: CompareSupplierRow, get: (d: any) => ReactNode) => asyncVal(stats[s.sup_id], get);
  const ctrlVal = (s: CompareSupplierRow, render: (r: SupplierControlsRaw, f: SupplierControlsFlags) => ReactNode) =>
    asyncVal(controls[s.sup_id], d => render(d, flagsFor(s)));

  // A yes/no/unknown value → green check / red cross / dash.
  const boolFlag = (v: boolean | null): ReactNode => (v === null ? NO_DATA : <Flag active={v} />);
  // A flag paired with a short label, centered.
  const flagWithText = (active: boolean, text: ReactNode): ReactNode => (
    <span className={styles.center}><Flag active={active} />{text}</span>
  );

  const colCount = visible.length;

  // Number formatting for the statistics section (Swedish grouping, optional unit).
  const fmtNum = (v: any) => (v === null || v === undefined || v === '' ? NO_DATA : Number(v).toLocaleString('sv-SE'));
  const withUnit = (v: any, unit: string) => { const n = fmtNum(v); return n === NO_DATA ? NO_DATA : `${n}${unit}`; };

  // Render helpers keep the table body declarative.
  const sectionRow = (title: string) => (
    <tr className={styles.section}>
      <td colSpan={colCount + 1}><span className={styles.sectionLabel}>{title}</span></td>
    </tr>
  );
  const dataRow = (
    key: string,
    label: ReactNode,
    render: (s: CompareSupplierRow) => ReactNode,
    opts?: { wrap?: boolean; titleFor?: (s: CompareSupplierRow) => string | undefined }
  ) => (
    <tr key={key}>
      <td className={styles.rowlabel}>{label}</td>
      {visible.map(s => {
        const value = render(s);
        // "No data" renders muted so it reads as absence rather than as real data at a glance;
        // "still loading" renders as a shimmering skeleton block (the purchasing plan's
        // loading-badge look) so a cell awaiting a response isn't mistaken for a settled "-".
        const isPlaceholder = value === NO_DATA;
        return (
          <td
            key={s.sup_id}
            className={`${styles.cell}${opts?.wrap ? ` ${styles.cellWrap}` : ''}${isPlaceholder ? ` ${styles.cellMuted}` : ''}`}
            title={opts?.titleFor?.(s)}
          >
            {value === LOADING ? <span className={styles.loadingCell} aria-hidden="true" /> : value}
          </td>
        );
      })}
    </tr>
  );
  // Kontroll rows share one error-tooltip rule: name the sources that failed for that column, so
  // a blank cell reads as "this provider couldn't be reached" rather than as "this company has no
  // such registration" — the same distinction the single-supplier panel draws with its "Saknas"
  // rows. Built once per fetch rather than per cell, since every Kontroll row asks for it.
  const ctrlErrorTitles = useMemo(() => {
    const labels: Record<SupplierControlsSource, string> = {
      syna: 'Syna',
      creditsafe: 'Creditsafe',
      tic: 'TIC',
      id06: 'ID06',
      monitoring: t('kontroll.monitored', 'Bevakad'),
      collectiveAgreement: t('kontroll.collectiveAgreement', 'Kollektivavtal'),
      sanctions: t('kontroll.sanctions', 'Sanktionslistor'),
    };
    const titles: Record<string, string> = {};
    Object.entries(controls).forEach(([supId, entry]) => {
      // A fetch that threw outright loses every source at once, so it gets the coarse message.
      if (entry.error) {
        titles[supId] = t('kontroll.errorHint', 'Kunde inte hämta kontrolldata.');
        return;
      }
      const failed = failedSources(entry.data);
      if (failed.length) {
        titles[supId] = t(
          'kontroll.sourceErrorHint',
          'Kunde inte hämta från {{sources}} – värden från dessa källor kan saknas eller vara inaktuella.',
          { sources: failed.map(source => labels[source]).join(', ') }
        );
      }
    });
    return titles;
  }, [controls, t]);

  const ctrlErrorTitle = (s: CompareSupplierRow): string | undefined => ctrlErrorTitles[s.sup_id];

  /**
   * The "last updated / refresh" row that heads the Ekonomi and Kontroll sections: a
   * refresh-everything button in the label column, then a per-column date + refresh button.
   *
   * Only the clicked button shows the spinner; its section-mates disable while the request runs.
   * `origin` records which button the user clicked ('all' or a sup_id) and both states derive
   * from it plus `sectionLoading`, so no explicit clearing is needed — a stale origin is inert
   * once the section stops loading. During the initial open no button was clicked (origin null),
   * so everything disables until the load settles, which also blocks refreshing mid-load.
   */
  const metaRow = (opts: {
    key: string;
    sectionLoading: boolean;
    origin: string | null;
    setOrigin: (origin: string) => void;
    onRefreshAll: () => void;
    dateFor: (s: CompareSupplierRow) => string | null;
    onRefreshOne: (s: CompareSupplierRow) => void;
    titleFor?: (s: CompareSupplierRow) => string | undefined;
  }) => {
    const spins = (id: string) => opts.sectionLoading && opts.origin === id;
    const locked = (id: string) => opts.sectionLoading && opts.origin !== id;
    return (
      <tr key={opts.key}>
        <td className={styles.rowlabel}>
          {anyOrgNumber && (
            <BtButton
              color="white"
              size="xxs"
              loaderShow={spins('all')}
              disabled={locked('all')}
              onClick={() => {
                // BtButton's disabled state blocks real clicks via pointer-events only; guard
                // here too so a synthetic/automated click can't move the spinner mid-request.
                if (locked('all') || spins('all')) return;
                opts.setOrigin('all');
                opts.onRefreshAll();
              }}
              leftIcon={<RefreshIcon style={{ fontSize: 14 }} />}
            >
              {t('economics.fetchAll', 'Uppdatera alla')}
            </BtButton>
          )}
        </td>
        {visible.map(s => {
          const date = opts.dateFor(s);
          return (
            <td key={s.sup_id} className={styles.meta} title={opts.titleFor?.(s)}>
              {date && <span className={styles.metaDate}>{t('economics.updatedAt', 'Uppdaterad {{date}}', { date })}</span>}
              {s.organizationNumber && (
                <div className={styles.center}>
                  <BtButton
                    color="white"
                    size="xxs"
                    loaderShow={spins(s.sup_id)}
                    disabled={locked(s.sup_id)}
                    onClick={() => {
                      if (locked(s.sup_id) || spins(s.sup_id)) return;
                      opts.setOrigin(s.sup_id);
                      opts.onRefreshOne(s);
                    }}
                    leftIcon={<RefreshIcon style={{ fontSize: 14 }} />}
                  >
                    {t('economics.fetchLatest', 'Hämta senaste')}
                  </BtButton>
                </div>
              )}
            </td>
          );
        })}
      </tr>
    );
  };

  /**
   * Kollektivavtal cell: a Ja/Nej flag, and — when the supplier does have agreements — a hover
   * tooltip listing them (the same data the single-supplier panel's "Visa avtal" drawer shows,
   * without having to leave this modal for it).
   */
  const renderCollectiveAgreementCell = (s: CompareSupplierRow): ReactNode => ctrlVal(s, r => {
    const active = deriveKollektivavtal(r);
    if (active === null) return NO_DATA;
    const flag = flagWithText(active, active ? t('kontroll.yes', 'Ja') : t('kontroll.no', 'Nej'));
    if (!active) return flag;
    const { rows, memberships } = deriveKollektivavtalDetails(r);
    if (rows.length === 0 && memberships.length === 0) return flag;
    return (
      <TooltipContainer
        className={styles.agreementTooltip}
        renderReferenceComponent={(className, ref) => (
          <span ref={ref} className={className} style={{ cursor: 'pointer' }}>{flag}</span>
        )}
      >
        {memberships.length > 0 && (
          <div className={styles.agreementMemberships}>
            {t('kontroll.collectiveAgreementMemberships', 'Medlem i {{list}}.', { list: memberships.join(', ') })}
          </div>
        )}
        {rows.length > 0 && (
          <table className={styles.agreementTable}>
            <thead>
              <tr>
                <th>{t('kontroll.collectiveAgreementTableUnion', 'Fackförbund')}</th>
                <th>{t('kontroll.collectiveAgreementTableAgreement', 'Avtal')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  <td>{row.source}</td>
                  <td>{row.agreement}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </TooltipContainer>
    );
  });

  return (
    <Modal
      show={show}
      setShow={setShow}
      hasCloseButton
      // Without this the table stays mounted after closing, so all ~30 rows × N columns
      // re-render on every unrelated interaction in the register behind it.
      unmountWhenHidden
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <CompareArrowsIcon style={{ fontSize: 22, color: 'var(--gray-500)' }} />
          {t('title', 'Jämför leverantörer')}
        </span>
      }
    >
      <div className={styles.compare} ref={setScrollEl}>
        <table className={styles.table} aria-label={t('tableAriaLabel', 'Jämförelse av leverantörer')}>
          <thead>
            <tr ref={setHeadRowEl}>
              <th className={styles.corner} />
              {visible.map(s => (
                <th key={s.sup_id} className={styles.colhead}>
                  <button
                    type="button"
                    className={styles.colheadRemove}
                    onClick={() => removeColumn(s.sup_id)}
                    aria-label={t('remove', 'Ta bort')}
                    title={t('remove', 'Ta bort')}
                  >
                    <CloseIcon style={{ fontSize: 16 }} />
                  </button>
                  <div className={styles.name}>
                    <Avatar s={s} />
                    <span className={styles.nameText} title={s.sup_name || undefined}>{s.sup_name || '-'}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* ALLMÄNT */}
            {sectionRow(t('section.general', 'Allmänt'))}
            {dataRow('kommun', t('row.municipality', 'Kommun'), s => cityLabel(s.city) ?? NO_DATA)}
            {dataRow('area', t('row.area', 'Yrkesområde'), s => {
              const areas = s.areaArray ?? [];
              if (!areas.length) return NO_DATA;
              const shown = areas.slice(0, MAX_VISIBLE_PILLS);
              const overflow = areas.slice(MAX_VISIBLE_PILLS);
              return (
                <div className={styles.pills}>
                  {shown.map(a => <Badge key={a.name} backgroundColor="var(--gray-100)">{a.name}</Badge>)}
                  {overflow.length > 0 && (
                    <TooltipContainer renderReferenceComponent={(className, ref) => (
                      <span ref={ref} className={className}>
                        <Badge className={styles.pillMore}>+{overflow.length}</Badge>
                      </span>
                    )}>
                      <div className={styles.pillOverflow}>
                        {overflow.map(a => <Badge key={a.name} backgroundColor="var(--gray-100)">{a.name}</Badge>)}
                      </div>
                    </TooltipContainer>
                  )}
                </div>
              );
            }, { wrap: true })}
            {dataRow('rating', t('row.rating', 'Rekommendationer'), s => (s.rating ? `★ ${formatRating(s.rating)}` : NO_DATA))}
            {dataRow('framework', t('row.frameworkAgreement', 'Ramavtal'), s => <Flag active={hasActiveFile(s.frameworkAgreements)} />)}
            {dataRow('liability', t('row.liabilityInsurance', 'Ansvarsförsäkring'), s => <Flag active={hasActiveFile(s.liabilityInsurances)} />)}

            {/* FÖRETAG (from the economics company info we already fetch) */}
            {sectionRow(t('section.company', 'Företag'))}
            {dataRow('orgnr', t('row.orgNumber', 'Org.nummer'), s => s.organizationNumber || econVal(s, d => d.companyInfo?.orgNumber ?? NO_DATA))}
            {/* Providers can return an empty kommun with a populated city (Syna when Bolsate is
                absent, CreditSafe when registeredMunicipality is), so fall back to city. */}
            {dataRow('sate', t('row.registeredSeat', 'Säte'), s => econVal(s, d => cityLabel(d.companyInfo?.kommun) || d.companyInfo?.city || NO_DATA))}
            {dataRow('companyForm', t('row.companyForm', 'Företagsform'), s => econVal(s, d => d.companyInfo?.ftgform ?? NO_DATA))}
            {dataRow('industry', t('row.industry', 'Bransch'), s => econVal(s, d => d.companyInfo?.bransch ?? NO_DATA))}

            {/* EKONOMI */}
            {sectionRow(t('section.economics', 'Ekonomi (senaste bokslut)'))}
            {metaRow({
              key: 'economicsMeta',
              sectionLoading: anyLoading(economics),
              origin: econOrigin,
              setOrigin: setEconOrigin,
              onRefreshAll: () => refreshAll(s => fetchEconomics(s.sup_id, s.organizationNumber, true)),
              // Economics carries a server-side fetchedAt; Kontroll stamps its own (see below).
              dateFor: s => (economics[s.sup_id]?.data?.fetchedAt ? formatDate(economics[s.sup_id].data.fetchedAt) : null),
              onRefreshOne: s => fetchEconomics(s.sup_id, s.organizationNumber, true),
              titleFor: s => {
                const e = economics[s.sup_id];
                return e?.error ? t('economics.errorHint', 'Kunde inte hämta ekonomidata ({{code}})', { code: e.error }) : undefined;
              },
            })}
            {dataRow('creditRating', t('row.creditRating', 'Kreditrating'), s => econVal(s, d => {
              const rating = d.ratingInfo?.rating;
              if (!rating) return NO_DATA;
              const tierClass = d.provider === 'syna'
                ? SYNA_RATING_CLASSES[String(rating)]
                : d.provider === 'creditsafe' ? CREDITSAFE_RATING_CLASSES[String(rating)] : undefined;
              return <span className={`${styles.ratingBadge}${tierClass ? ` ${tierClass}` : ''}`}>{rating}</span>;
            }))}
            {dataRow('revenue', t('row.revenue', 'Omsättning'), s => econVal(s, d => fmtKpi(d.kpiData?.[0]?.omsattning, 'tkr')))}
            {dataRow('result', t('row.result', 'Resultat'), s => econVal(s, d => {
              const formatted = fmtKpi(d.kpiData?.[0]?.resultat, 'tkr');
              const n = Number(String(d.kpiData?.[0]?.resultat ?? '').replace(/\s/g, '').replace(',', '.'));
              if (formatted === NO_DATA || isNaN(n) || n === 0) return formatted;
              return <span className={n > 0 ? styles.deltaPositive : styles.deltaNegative}>{formatted}</span>;
            }))}
            {dataRow('solidity', t('row.solidity', 'Soliditet'), s => econVal(s, d => fmtKpi(d.kpiData?.[0]?.soliditet, 'pct')))}
            {dataRow('econEmployees', t('row.employees', 'Anställda'), s => econVal(s, d => fmtKpi(d.kpiData?.[0]?.anstallda, 'count')))}

            {/* KONTROLL */}
            {sectionRow(t('section.kontroll', 'Kontroll'))}
            {metaRow({
              key: 'kontrollMeta',
              sectionLoading: anyLoading(controls),
              origin: ctrlOrigin,
              setOrigin: setCtrlOrigin,
              onRefreshAll: () => refreshAll(s => fetchControls(s, true)),
              // Only claim freshness when every source actually answered — otherwise the stamp
              // would vouch for values a failed provider had just blanked. The tooltip says which.
              dateFor: s => (controls[s.sup_id]?.fetchedAt && !ctrlErrorTitles[s.sup_id]
                ? formatDate(controls[s.sup_id].fetchedAt)
                : null),
              onRefreshOne: s => fetchControls(s, true),
              titleFor: ctrlErrorTitle,
            })}
            {dataRow('fskatt', t('kontroll.fskatt', 'F-skatt'), s => ctrlVal(s, (r, f) => boolFlag(deriveFskatt(r, f))), { titleFor: ctrlErrorTitle })}
            {dataRow('moms', t('kontroll.moms', 'Reg. för moms'), s => ctrlVal(s, (r, f) => boolFlag(deriveMoms(r, f))), { titleFor: ctrlErrorTitle })}
            {dataRow('employer', t('kontroll.employer', 'Reg. arbetsgivare'), s => ctrlVal(s, (r, f) => boolFlag(deriveArbetsgivare(r, f))), { titleFor: ctrlErrorTitle })}
            {dataRow('postponedTax', t('kontroll.postponedTax', 'Skatteanstånd'), s => ctrlVal(s, (r, f) => deriveSkatteanstand(r, f) ?? NO_DATA), { titleFor: ctrlErrorTitle })}
            {dataRow(
              'collectiveAgreement',
              <InfoTooltip label={t('kontroll.collectiveAgreement', 'Kollektivavtal')}>
                {tControls('collectiveAgreement.tooltip.text', 'Vi visar data från tjänsten Kollektivavtalskollen som inkluderar avtal med Byggnads, Kommunal, Fastighet, Elektrikerna, Handels, Målarna, Akavia, Forena och Finansförbundet. Vi hämtar också data från Bygg- och installationssektorn, som inkluderar medlemskap, kollektivavtal och hängavtal från Byggföretagen, Installatörsföretagen, Maskinentreprenörerna, Plåt & Ventföretagen, Måleriföretagen och Glasbranschföreningen.')}
              </InfoTooltip>,
              renderCollectiveAgreementCell,
              { titleFor: ctrlErrorTitle }
            )}
            {dataRow(
              'sanctions',
              <InfoTooltip label={t('kontroll.sanctions', 'Sanktionslistor')}>
                {tControls('sanctions.tooltip.text', 'Sanktionslistor uppdateras varje natt. Vi kontrollerar företagsnamn och nyckelpersoner kopplade till företaget.')}
              </InfoTooltip>,
              s => ctrlVal(s, r => {
                const x = deriveSanctions(r);
                if (x.label === 'unknown') return NO_DATA;
                if (x.label === 'refreshing') return t('kontroll.sanctionsRefreshing', 'Uppdateras…');
                if (x.label === 'clear') return flagWithText(true, t('kontroll.sanctionsClear', 'Förekommer inte'));
                return <span title={x.names.join(', ') || undefined} style={{ color: 'var(--red-500)' }}>{t('kontroll.sanctionsMatch', '{{count}} träffar', { count: x.matches })}</span>;
              }),
              { titleFor: ctrlErrorTitle }
            )}
            {dataRow('id06', t('kontroll.id06', 'ID06'), s => ctrlVal(s, r => {
              const x = deriveId06(r);
              if (x.error || x.status === null) return NO_DATA;
              return flagWithText(x.status, x.status ? t('kontroll.id06Cards', '{{count}} kort', { count: x.cards ?? 0 }) : t('kontroll.no', 'Nej'));
            }), { titleFor: ctrlErrorTitle })}
            {dataRow('employerFees', t('kontroll.employerFees', 'Arbetsgivaravgifter'), s => ctrlVal(s, r => deriveArbetsgivaravgifter(r) ?? NO_DATA), { titleFor: ctrlErrorTitle })}
            {dataRow('fteEstimate', t('kontroll.fteEstimate', 'Antal heltidsanställda (uppskattat)'), s => ctrlVal(s, r => { const n = deriveHeltidEstimate(r); return n === null ? NO_DATA : `≤ ${n}`; }), { titleFor: ctrlErrorTitle })}
            {dataRow('monitored', t('kontroll.monitored', 'Bevakad'), s => ctrlVal(s, (r, f) => {
              const x = deriveBevakad(r, f);
              if (x.monitored === null) {
                return (
                  <TooltipContainer
                    className={styles.tooltip}
                    renderReferenceComponent={(className, ref) => (
                      <span ref={ref} className={`${className} ${styles.unknown}`} >
                        <WarningOutlineIcon />
                      </span>
                    )}
                  >
                    {t('kontroll.monitoredUnknown', 'Kunde inte hämta bevakningsstatus.')}
                  </TooltipContainer>
                );
              }
              const label = x.monitored ? (x.since ? t('kontroll.monitoredSince', 'sedan {{date}}', { date: x.since }) : t('kontroll.yes', 'Ja')) : t('kontroll.no', 'Nej');
              const title = x.monitored && x.lastEvent ? t('kontroll.monitoredLastEvent', 'Senaste händelse {{date}}', { date: x.lastEvent }) : undefined;
              return <span title={title}>{flagWithText(x.monitored, label)}</span>;
            }), { titleFor: ctrlErrorTitle })}

            {sectionRow(t('section.statistics', 'Statistik (alla år)'))}
            {dataRow('projects', t('row.projectsCount', 'Antal projekt'), s => s.projectsCount || 0)}
            {dataRow('lastRated', t('row.lastRated', 'Senast betygsatt'), s => (s.lastRatedAt ? moment(s.lastRatedAt).format('YYYY-MM-DD') : NO_DATA))}
            {dataRow('sentRequests', t('row.sentBidRequests', 'Skickade förfrågningar'), s => statVal(s, d => fmtNum(d.sentBidRequests)))}
            {dataRow('submittedBids', t('row.submittedBids', 'Inlämnade anbud'), s => statVal(s, d => fmtNum(d.submittedBids)))}
            {dataRow('bidResponseRate', t('row.bidResponseRate', 'Anbudsfrekvens'), s => statVal(s, d => withUnit(d.bidResponseRate, '%')))}
            {dataRow('responseRate', t('row.responseRate', 'Svarsfrekvens'), s => statVal(s, d => withUnit(d.responseRate, '%')))}
            {dataRow('jobsWon', t('row.jobsWon', 'Vunna uppdrag'), s => statVal(s, d => fmtNum(d.jobsWon)))}
            {dataRow('contractedAmount', t('row.totalContractedAmount', 'Total kontraktssumma'), s => statVal(s, d => withUnit(d.totalContractedAmount, ' kr')))}
            {dataRow('ataAverage', t('row.ataAverage', 'ÄTA % genomsnitt'), s => statVal(s, d => withUnit(d.ataAverage, '%')))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
};

export default CompareSuppliersModal;
